import { type Context, Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { getCsrfToken } from 'src/csrf';
import {
  EVENT_TASK_INPUT_FOCUS,
  EVENT_TASK_INPUT_HIGHLIGHT,
  EVENT_TASK_LIVE_FINISH_REQUEST,
  EVENT_TASK_LIVE_PENDING,
  EVENT_TASK_LIVE_START_REQUEST,
  EVENT_TASK_MESSAGE_FOCUS,
  EVENT_TASK_REOPENED,
  EVENT_TASK_RESOLVED,
} from 'src/events';
import { toggleAttribute } from 'src/utils/dom-toggle';

// Seems that browsers don't always like this.
const ENABLE_SAME_FRAME_FOCUS = false;

const MAX_DEBOUNCE_LIVE_UPDATE = 2500;
const MIN_DEBOUNCE_LIVE_UPDATE = 1000;

export default class TasksLiveController extends Controller {
  public static values = { url: String };
  public static targets = [];

  declare private urlValue: string;

  private connected: boolean = false;
  private abortController: AbortController | undefined;
  private storedInputValues: Record<string, null | string | string[]> = {};
  private changedInputValues: Record<
    string,
    {
      type: 'input' | 'label' | 'toggle';
      value: null | string | string[];
      messageIds: string[];
      submitting: boolean;
    }
  > = {};

  private debounceLiveUpdateTimer: ReturnType<typeof setTimeout> | undefined;
  private lastScheduledRequest: number | undefined;
  private activeLiveUpdate: Promise<unknown> | undefined;

  constructor(context: Context) {
    super(context);

    this.onMessageRequestFocus = this.onMessageRequestFocus.bind(this);
    this.onInputRequestFocus = this.onInputRequestFocus.bind(this);
    this.onInputHighlight = this.onInputHighlight.bind(this);
    this.onInputChanged = this.onInputChanged.bind(this);
    this.onTaskResolved = this.onTaskResolved.bind(this);
    this.onTaskReopened = this.onTaskReopened.bind(this);

    this.debounceLiveUpdate = this.debounceLiveUpdate.bind(this);
    this.requestLiveUpdate = this.requestLiveUpdate.bind(this);
    this.preventNavigation = this.preventNavigation.bind(this);
  }

  connect(): void {
    this.connected = true;
    this.abortController = new AbortController();

    this.element.addEventListener(
      EVENT_TASK_MESSAGE_FOCUS,
      this.onMessageRequestFocus,
    );
    this.element.addEventListener(
      EVENT_TASK_INPUT_FOCUS,
      this.onInputRequestFocus,
    );
    this.element.addEventListener(
      EVENT_TASK_INPUT_HIGHLIGHT,
      this.onInputHighlight,
    );
    this.element.addEventListener(EVENT_TASK_RESOLVED, this.onTaskResolved);
    this.element.addEventListener(EVENT_TASK_REOPENED, this.onTaskReopened);

    this.storedInputValues = {};

    this.element.querySelectorAll('ds-input').forEach((input) => {
      this.upgradeInput(input);
    });

    console.debug('[task-live] initial values', this.storedInputValues);
  }

  disconnect(): void {
    this.connected = false;
    this.abortController?.abort('TasksLiveController#disconnect');

    window.removeEventListener('beforeunload', this.preventNavigation);

    this.element.removeEventListener(
      EVENT_TASK_MESSAGE_FOCUS,
      this.onMessageRequestFocus,
    );
    this.element.removeEventListener(
      EVENT_TASK_INPUT_FOCUS,
      this.onInputRequestFocus,
    );
    this.element.removeEventListener(
      EVENT_TASK_INPUT_HIGHLIGHT,
      this.onInputHighlight,
    );
    this.element.removeEventListener(EVENT_TASK_RESOLVED, this.onTaskResolved);
    this.element.removeEventListener(EVENT_TASK_REOPENED, this.onTaskReopened);
  }

  private upgradeInput(input: Element) {
    const name = input.getAttribute('ds-name');
    const type = input.getAttribute('ds-type');
    const value = input.getAttribute('ds-value');

    if (!name) {
      return;
    }

    switch (type) {
      case 'hidden':
      case 'text':
      case 'url':
      case 'date':
      case 'time':
      case 'datetime-local':
      case 'email':
      case 'number':
      case 'asset':
      case 'contractor':
      case 'employee':
      case 'project':
      case 'site': {
        this.storedInputValues[name] = value;
        break;
      }

      case 'textarea': {
        // Check if correct

        this.storedInputValues[name] = value;
        break;
      }

      case 'choice':
      case 'label':
      case 'radio': {
        this.storedInputValues[name] ||= null;
        if (
          input.hasAttribute('checked') &&
          input.getAttribute('checked') !== 'false'
        ) {
          this.storedInputValues[name] = value;
        }
        break;
      }

      case 'checkbox':
      case 'labels': {
        this.storedInputValues[name] ||= [];

        if (
          input.hasAttribute('checked') &&
          input.getAttribute('checked') !== 'false' &&
          value !== null
        ) {
          (this.storedInputValues[name] as string[]).push(value);
        }
        break;
      }

      case 'button':
      case 'toggle': {
        this.storedInputValues[name] ||= null;
        if (
          input.hasAttribute('aria-pressed') &&
          input.getAttribute('aria-pressed') !== 'false'
        ) {
          this.storedInputValues[name] = value;
        }
        break;
      }

      case 'switch': {
        this.storedInputValues[name] ||= null;
        if (
          input.hasAttribute('aria-checked') &&
          input.getAttribute('aria-checked') !== 'false'
        ) {
          this.storedInputValues[name] = value;
        }
        break;
      }

      case 'assets':
      case 'contractors':
      case 'employees':
      case 'projects':
      case 'sites': {
        this.storedInputValues[name] ||= [];

        if (value !== null) {
          (this.storedInputValues[name] as string[]).push(value);
        }
        break;
      }
    }
  }

  public onMessageRequestFocus(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const customEvent = event as Omit<CustomEvent, 'detail'> & {
      detail: {
        id: string;
      };
    };

    if (!('id' in customEvent.detail)) {
      throw new Error(
        'Expected id in detail, actual: ' +
          Object.keys(customEvent.detail).toString(),
      );
    }

    const { id } = customEvent.detail;
    this.focusMessage(id);
  }

  public onInputRequestFocus(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const customEvent = event as Omit<CustomEvent, 'detail'> & {
      detail: {
        dsName: string;
      };
    };

    if (!('dsName' in customEvent.detail)) {
      throw new Error(
        'Expected dsName in detail, actual: ' +
          Object.keys(customEvent.detail).toString(),
      );
    }

    const { dsName } = customEvent.detail;
    this.focusInput(dsName);
  }

  public onInputHighlight(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const customEvent = event as CustomEvent as Omit<CustomEvent, 'detail'> & {
      detail: {
        dsName: string;
        state: 'error';
      };
    };

    if (!('dsName' in customEvent.detail) || !('state' in customEvent.detail)) {
      throw new Error(
        'Expected dsName and state in detail, actual: ' +
          Object.keys(customEvent.detail).toString(),
      );
    }

    const { dsName, state } = customEvent.detail;
    this.highlightInput(
      dsName.toLocaleLowerCase(),
      state.toLocaleLowerCase() as typeof state,
    );
  }

  public onTaskResolved(_event: Event) {}

  public onTaskReopened(_event: Event) {}

  public onInputChanged(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const customEvent = event as CustomEvent as Omit<CustomEvent, 'detail'> & {
      detail: {
        dsName: string;
        type: 'input' | 'label' | 'toggle';
        value: string | string[];
        messageId: string | undefined;
      };
    };

    if (!('dsName' in customEvent.detail) || !('value' in customEvent.detail)) {
      throw new Error(
        'Expected dsName and value in detail, actual: ' +
          Object.keys(customEvent.detail).toString(),
      );
    }

    // This overrides any last known value we have
    const { messageId, dsName, value, type } = customEvent.detail;

    if (!this.changedInputValues[dsName]) {
      this.changedInputValues[dsName] = {
        type,
        value,
        submitting: false,
        messageIds: [],
      };
    }

    this.changedInputValues[dsName].value = value;
    this.changedInputValues[dsName].submitting = false;

    if (
      messageId &&
      !this.changedInputValues[dsName].messageIds.includes(messageId)
    ) {
      this.changedInputValues[dsName].messageIds.push(messageId);
    }

    this.debounceLiveUpdate();
  }

  public focusMessage(messageId: string) {
    const elementId = `message-${messageId}`;

    // Element may be injected this frame, so focus next frame
    const element = document.getElementById(elementId);
    if (element && ENABLE_SAME_FRAME_FOCUS) {
      element.focus();
      console.debug(
        '[task-live] focus message',
        element,
        document.activeElement,
      );
      return;
    }

    console.debug('[task-live] focus message (next frame)', elementId);
    setTimeout(() => {
      const focusable = document.getElementById(elementId);
      focusable?.focus();
    }, 0);
  }

  public onInputFocusRequested(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const customEvent = event as CustomEvent as Omit<CustomEvent, 'detail'> & {
      detail: {
        dsName: string;
      };
    };

    if (!('dsName' in customEvent.detail)) {
      throw new Error(
        'Expected dsName and value in detail, actual: ' +
          Object.keys(customEvent.detail).toString(),
      );
    }

    const dsName = customEvent.detail.dsName;
    const dsInput = this.element.querySelector(
      `ds-input[ds-name="${dsName}" i]`,
    );

    if (!dsInput) {
      return;
    }

    const focusable = dsInput.querySelector<
      | HTMLFieldSetElement
      | HTMLTextAreaElement
      | HTMLInputElement
      | HTMLButtonElement
    >(
      'textarea:not([hidden]),input:not([type="hidden"]):not([hidden]),button:not([hidden])',
    );

    console.debug('[task-live] focus input', dsName, dsInput, focusable);

    focusable?.focus({ focusVisible: true });
  }

  public focusInput(dsName: string) {
    const dsInput = this.element.querySelector(
      `ds-input[ds-name="${dsName}" i]`,
    );
    if (!dsInput) {
      console.warn(
        '[task-live] requested focus on non-existent input: ' + dsName,
      );
      return;
    }

    console.debug('[task-live] focus input (next frame)', dsName);
    setTimeout(() => {
      const focusable = dsInput.querySelector<
        | HTMLFieldSetElement
        | HTMLTextAreaElement
        | HTMLInputElement
        | HTMLButtonElement
      >(
        'textarea:not([hidden]),input:not([type="hidden"]):not([hidden]),button:not([hidden])',
      );
      focusable?.focus({ focusVisible: true });
    }, 0);
  }

  public highlightInput(dsName: string, state: 'error') {
    const dsInput = this.element.querySelector(
      `ds-input[ds-name="${dsName}" i]`,
    );
    if (!dsInput) {
      console.warn(
        '[task-live] requested highlight on non-existent input: ' + dsName,
      );
      return;
    }

    console.debug('[task-live] highlight input (next frame)', dsName);
    setTimeout(() => {
      const highlightable = dsInput.querySelector<
        | HTMLFieldSetElement
        | HTMLTextAreaElement
        | HTMLInputElement
        | HTMLButtonElement
      >('fieldset,textarea,input:not([type="hidden"]),button');
      if (highlightable) {
        switch (state) {
          case 'error': {
            highlightable.setAttribute('aria-invalid', 'true');
          }
        }
      }
    }, 0);
  }

  private debounceLiveUpdate() {
    this.dispatch(EVENT_TASK_LIVE_PENDING, { detail: {} });

    clearTimeout(this.debounceLiveUpdateTimer);

    if (this.lastScheduledRequest === undefined) {
      this.lastScheduledRequest = +new Date();

      // Pending changes
      window.addEventListener('beforeunload', this.preventNavigation);
      console.debug('[task-live] prevent navigation + ');
    }

    this.debounceLiveUpdateTimer = setTimeout(
      this.requestLiveUpdate,
      this.debounceRequestTimeout,
    );
  }

  private get debounceRequestTimeout() {
    const proposedDelay =
      new Date().getTime() - (this.lastScheduledRequest || 0);
    return Math.max(
      Math.min(MAX_DEBOUNCE_LIVE_UPDATE, proposedDelay),
      MIN_DEBOUNCE_LIVE_UPDATE,
    );
  }

  private requestLiveUpdate() {
    console.debug('[task-live] request a live update');

    // Debounce is done. Request a live update unless there is one ongoing.
    if (this.activeLiveUpdate) {
      return;
    }

    this.activeLiveUpdate = this.doLiveUpdate().finally(() => {
      // Which should unset active when done
      this.activeLiveUpdate = undefined;

      // Nothing more pending
      if (this.hasPendingChanges) {
        return this.requestLiveUpdate();
      }

      this.lastScheduledRequest = undefined;
      window.removeEventListener('beforeunload', this.preventNavigation);
      console.debug('[task-live] prevent navigation - ');
    });
  }

  private get hasPendingChanges() {
    return Object.values(this.changedInputValues).some(
      ({ submitting }) => !submitting,
    );
  }

  private async doLiveUpdate() {
    // Abort if entire thing is aborted
    if (this.abortController?.signal.aborted) {
      return;
    }

    // Do a live update
    const changes = Object.entries(this.changedInputValues).filter(
      ([_, { submitting }]) => !submitting,
    );

    if (changes.length === 0) {
      // Nothing to submit
      console.debug('[task-live] no live update changes');
      return;
    }

    this.dispatch(EVENT_TASK_LIVE_START_REQUEST, {
      detail: { count: changes.length },
    });

    const body: {
      live: {
        input: Record<string, string | string[] | null>;
        toggle: Record<string, string>;
        label: Record<string, string | string[]>;
      };
      messages: Record<string, string[]>;
    } = {
      live: {
        input: {},
        toggle: {},
        label: {},
      },
      messages: {},
    };

    changes.forEach(([key, { type, value, messageIds }]) => {
      this.changedInputValues[key].submitting = true;
      body.live[type][key] = value;

      messageIds.forEach((messageId) => {
        body.messages[messageId] ||= [];
        body.messages[messageId].push(key);
      });
    });

    console.debug('[task-live] live update', changes);

    await fetch(this.urlValue, {
      method: 'POST',
      headers: {
        accept:
          'application/vnd.opus-safety.task-live-update-response.v1+json, text/vnd.turbo-stream.html;q=0.5, application/problem+json;q=0.1',
        'content-type': 'application/vnd.opus-safety.task-live-update.v1+json',
        'X-CSRF-Token': getCsrfToken() || '',
      },
      body: JSON.stringify(body),
      signal: this.abortController?.signal,
    })
      .then(async (response) => {
        switch (
          (response.headers.get('content-type') || '').split(';').shift()
        ) {
          case 'application/vnd.opus-safety.task-live-update-response.v1+json': {
            // TODO
            break;
          }

          case 'text/vnd.turbo-stream.html': {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            Turbo.renderStreamMessage(await response.text());
            break;
          }

          case 'application/problem+json': {
            // TODO: proper fetching etc.
            const problem = (await response.json()) as { title: string };
            console.error({ problem });

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            Turbo.renderStreamMessage(`<turbo-stream action="update" target="turbo-stream-status-messages">
              <template>
                <div class="rounded-none bg-red-50 dark:bg-red-900 py-4" data-controller="dismissible" data-turbo-temporary>
                  <div class="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex min-w-0 items-center relative">
                    <div class="shrink-0">
                      <svg class="size-5 mx-0.5 text-red-400 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="ml-3 mr-10">
                      <p class="text-sm font-medium text-red-800 dark:text-red-100">
                        ${problem
                          .title
                          .split('\n')
                          .map((line) => `${line}<br>`)
                          .join('\n')}
                      </p>
                    </div>
                    <button type="button" class="js-only absolute right-4 size-10 cursor-pointer rounded-full hover:bg-red-100 active:bg-red-50 dark:hover:bg-red-800 dark:active:bg-red-950" data-action="dismissible#onDismiss">
                      <span aria-hidden="true">&times;</span><span class="sr-only">Dismiss notice</span>
                    </button>
                  </div>
                </div>
              </template>
            </turbo-stream>`);
            break;
          }

          default: {
            throw new Error(
              'Unexpected response type: ' +
                response.headers.get('content-type'),
            );
          }
        }
      })
      .catch((e: unknown) => {
        this.dispatch(EVENT_TASK_LIVE_FINISH_REQUEST, {
          detail: { count: changes.length, success: false, error: e },
        });

        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        return Promise.reject(e);
      })
      .finally(() =>
        window.removeEventListener('beforeunload', this.preventNavigation),
      );

    this.dispatch(EVENT_TASK_LIVE_FINISH_REQUEST, {
      detail: { count: changes.length, success: true },
    });

    console.debug(
      '[task-live] live update complete',
      changes,
      this.changedInputValues,
    );

    changes.forEach(([key, { value }]) => {
      // Track last known value on server
      this.storedInputValues[key] = value;

      // Checks if there has been a change to the changed input since. If so
      // it is now stored and we are done with that input.
      if (this.changedInputValues[key].submitting) {
        delete this.changedInputValues[key];
      }
    });

    Object.entries(this.changedInputValues).forEach(([key, entry]) => {
      this.element
        .querySelectorAll(`ds-input[ds-name='${key}' i]`)
        .forEach((dsInput) => {
          switch (dsInput.getAttribute('ds-type')) {
            case 'checkbox':
            case 'labels': {
              const checks = dsInput.querySelectorAll<HTMLInputElement>(
                `input[type="checkbox"]`,
              );
              checks.forEach((check) =>
                toggleAttribute(
                  'checked',
                  check,
                  Array(entry.value!).includes(check.value),
                ),
              );

              console.info(
                '[task-live] restore pending input',
                key,
                entry,
                checks,
              );
              break;
            }

            case 'radio':
            case 'label': {
              const radios =
                dsInput.querySelectorAll<HTMLInputElement>(
                  `input[type="radio"]`,
                );
              const input = dsInput.querySelector<HTMLInputElement>(
                `input[value="${entry.value as string}"]`,
              );
              if (input) {
                radios.forEach((radio) =>
                  toggleAttribute('checked', radio, false),
                );
                toggleAttribute('checked', input, true);
              }

              console.info(
                '[task-live] restore pending input',
                key,
                entry,
                radios,
                input,
              );
              break;
            }

            default: {
              const input = dsInput.querySelector('input');
              if (input) {
                input.setAttribute('value', (entry.value as string) ?? '');
              }

              console.info(
                '[task-live] restore pending input',
                key,
                entry,
                input,
              );
            }
          }
        });
    });
  }

  private preventNavigation(event: Event) {
    event.preventDefault();

    // Included for legacy support, e.g. Chrome/Edge < 119
    if ('returnValue' in event) {
      event.returnValue = true;
    }
  }

  public onAssigned(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const stimulusAction = event as Omit<CustomEvent, 'detail'> & {
      detail: {
        userId: string;
      };
    };
    if (!('userId' in stimulusAction.detail)) {
      throw new Error(
        'Expected userId in detail, actual: ' +
          Object.keys(stimulusAction.detail).toString(),
      );
    }

    // const { userId } = stimulusAction.detail;
    //
  }

  public onToggleAction(event: Event) {
    if (
      !('params' in event) ||
      typeof event.params !== 'object' ||
      !event.params
    ) {
      throw new Error('Expected custom event with params property');
    }

    const stimulusAction = event as Omit<CustomEvent, 'params'> & {
      params: {
        toggle: string;
        pressed: string;
      };
    };

    if (
      !('toggle' in stimulusAction.params) ||
      !('pressed' in stimulusAction.params)
    ) {
      throw new Error(
        'Expected userId in params, actual: ' +
          Object.keys(stimulusAction.params).toString(),
      );
    }

    const { toggle, pressed } = stimulusAction.params;

    if (!this.changedInputValues[toggle]) {
      this.changedInputValues[toggle] = {
        type: 'toggle',
        value: '',
        submitting: false,
        messageIds: [],
      };
    }

    this.changedInputValues[toggle].value = pressed ? '' : '1';
    this.changedInputValues[toggle].submitting = false;

    if (event.target instanceof Element) {
      const button = event.target.closest('button');
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-pressed', String(!pressed));
      }
    }

    this.debounceLiveUpdate();

    console.info(event);
  }
}
